import React, { useEffect, useRef, useState } from 'react';
import { InstallLaborLine, TravelNodeData } from '../../types';
import { ProposalRateSettings } from '../../utils/appSettings';
import { MapPin, RefreshCw, Check, AlertCircle } from 'lucide-react';
import { computeTravelNodeRawTotal, withDefaultHotelNights } from '../../utils/proposalTravelTotals';
import TravelBreakdownGroups from './TravelBreakdownGroups';

interface TravelInputsProps {
  data: TravelNodeData;
  rates: ProposalRateSettings;
  installDays: number;
  laborLines?: InstallLaborLine[];
  onUpdate: (data: TravelNodeData) => void;
  destinationCity?: string; // Optional: "City, State" format for auto-calculation
}

// Approximate distance calculation using Haversine formula
// Basic US city coordinates for fallback
const CITY_COORDS: Record<string, { lat: number; lng: number }> = {
  'franklin, tn': { lat: 35.9251, lng: -86.8689 },
  'nashville, tn': { lat: 36.1627, lng: -86.7816 },
  'atlanta, ga': { lat: 33.749, lng: -84.388 },
  'birmingham, al': { lat: 33.5207, lng: -86.8025 },
  'memphis, tn': { lat: 35.1495, lng: -90.049 },
  'knoxville, tn': { lat: 35.9606, lng: -83.9207 },
  'chattanooga, tn': { lat: 35.0456, lng: -85.3097 },
  'lexington, ky': { lat: 38.0406, lng: -84.5037 },
  'louisville, ky': { lat: 38.2527, lng: -85.7585 },
  'st. louis, mo': { lat: 38.627, lng: -90.1994 },
  'charlotte, nc': { lat: 35.2271, lng: -80.8431 },
  'raleigh, nc': { lat: 35.7796, lng: -78.6382 },
  'columbia, sc': { lat: 34.0007, lng: -81.0348 },
  'jacksonville, fl': { lat: 30.3322, lng: -81.6557 },
  'orlando, fl': { lat: 28.5383, lng: -81.3792 },
  'tampa, fl': { lat: 27.9506, lng: -82.4572 },
  'miami, fl': { lat: 25.7617, lng: -80.1918 },
  'new orleans, la': { lat: 29.9511, lng: -90.0715 },
  'dallas, tx': { lat: 32.7767, lng: -96.797 },
  'houston, tx': { lat: 29.7604, lng: -95.3698 },
  'austin, tx': { lat: 30.2672, lng: -97.7431 },
  'san antonio, tx': { lat: 29.4241, lng: -98.4936 },
  'chicago, il': { lat: 41.8781, lng: -87.6298 },
  'indianapolis, in': { lat: 39.7684, lng: -86.1581 },
  'cincinnati, oh': { lat: 39.1031, lng: -84.512 },
  'columbus, oh': { lat: 39.9612, lng: -82.9988 },
  'cleveland, oh': { lat: 41.4993, lng: -81.6944 },
  'detroit, mi': { lat: 42.3314, lng: -83.0458 },
  'denver, co': { lat: 39.7392, lng: -104.9903 },
  'phoenix, az': { lat: 33.4484, lng: -112.074 },
  'los angeles, ca': { lat: 34.0522, lng: -118.2437 },
  'san francisco, ca': { lat: 37.7749, lng: -122.4194 },
  'seattle, wa': { lat: 47.6062, lng: -122.3321 },
  'portland, or': { lat: 45.5152, lng: -122.6784 },
  'new york, ny': { lat: 40.7128, lng: -74.006 },
  'boston, ma': { lat: 42.3601, lng: -71.0589 },
  'philadelphia, pa': { lat: 39.9526, lng: -75.1652 },
  'washington, dc': { lat: 38.9072, lng: -77.0369 },
  'baltimore, md': { lat: 39.2904, lng: -76.6122 },
  'pittsburgh, pa': { lat: 40.4406, lng: -79.9959 },
};

const FRANKLIN_TN = CITY_COORDS['franklin, tn'];

// Haversine formula to calculate distance between two points
const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(R * c * 1.2); // Add 20% for road distance approximation
};

const TravelInputs: React.FC<TravelInputsProps> = ({ data, rates, installDays, laborLines, onUpdate, destinationCity }) => {
  const [localData, setLocalData] = useState(data);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calcStatus, setCalcStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [calcMessage, setCalcMessage] = useState('');
  const prevInstallDaysRef = useRef(installDays);

  useEffect(() => {
    setLocalData(data);
  }, [data]);

  useEffect(() => {
    const previousInstallDays = prevInstallDaysRef.current;
    prevInstallDaysRef.current = installDays;
    const next = withDefaultHotelNights(data, installDays, previousInstallDays, data.travelDays);
    if (next.hotelNights === data.hotelNights) return;
    const updated = {
      ...next,
      total: computeTravelNodeRawTotal(next, rates, laborLines),
    };
    setLocalData(updated);
    onUpdate(updated);
  }, [installDays]);

  // Auto-calculate distance when destinationCity is provided and miles is 0
  useEffect(() => {
    if (destinationCity && localData.milesToJobSite === 0) {
      calculateMileage();
    }
  }, [destinationCity]);

  const calculateMileage = async () => {
    if (!destinationCity) {
      setCalcStatus('error');
      setCalcMessage('No destination city provided');
      return;
    }

    setIsCalculating(true);
    setCalcStatus('idle');

    try {
      // Try to find city in our lookup table
      const normalizedCity = destinationCity.toLowerCase().trim();

      // Check for exact match or partial match
      let destCoords = CITY_COORDS[normalizedCity];

      if (!destCoords) {
        // Try to find partial match (e.g., "Atlanta" matches "atlanta, ga")
        const cityPart = normalizedCity.split(',')[0].trim();
        const matchKey = Object.keys(CITY_COORDS).find(key => key.includes(cityPart));
        if (matchKey) {
          destCoords = CITY_COORDS[matchKey];
        }
      }

      if (destCoords) {
        const distance = calculateDistance(
          FRANKLIN_TN.lat, FRANKLIN_TN.lng,
          destCoords.lat, destCoords.lng
        );
        handleChange('milesToJobSite', distance);
        setCalcStatus('success');
        setCalcMessage(`~${distance} miles to ${destinationCity}`);
      } else {
        // Try using OpenStreetMap Nominatim API (free, no API key)
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(destinationCity)}&countrycodes=us&limit=1`,
          { headers: { 'User-Agent': 'LCTF-Proposal-Builder' } }
        );

        if (response.ok) {
          const results = await response.json();
          if (results && results.length > 0) {
            const { lat, lon } = results[0];
            const distance = calculateDistance(
              FRANKLIN_TN.lat, FRANKLIN_TN.lng,
              parseFloat(lat), parseFloat(lon)
            );
            handleChange('milesToJobSite', distance);
            setCalcStatus('success');
            setCalcMessage(`~${distance} miles to ${destinationCity}`);
          } else {
            throw new Error('City not found');
          }
        } else {
          throw new Error('Geocoding service unavailable');
        }
      }
    } catch (error) {
      setCalcStatus('error');
      setCalcMessage('Could not calculate - enter manually');
    } finally {
      setIsCalculating(false);
      setTimeout(() => setCalcStatus('idle'), 5000);
    }
  };

  const handlePatch = (patch: Partial<TravelNodeData>) => {
    let updated = { ...localData, ...patch };
    if (patch.travelDays !== undefined && patch.hotelNights === undefined) {
      updated = withDefaultHotelNights(updated, installDays, installDays, localData.travelDays);
    }
    updated.total = computeTravelNodeRawTotal(updated, rates, laborLines);
    setLocalData(updated);
    onUpdate(updated);
  };

  const handleChange = (field: keyof TravelNodeData, value: number) => {
    handlePatch({ [field]: value });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="node-inputs">
      <div className="input-section">
        <h4><MapPin size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />Travel from {rates.homeBaseAddress}</h4>

        {destinationCity && (
          <div className="destination-display">
            <span className="destination-label">To:</span>
            <span className="destination-city">{destinationCity}</span>
            <button
              className="calc-distance-btn"
              onClick={calculateMileage}
              disabled={isCalculating}
              title="Recalculate distance"
            >
              <RefreshCw size={14} className={isCalculating ? 'spinning' : ''} />
              {isCalculating ? 'Calculating...' : 'Calculate'}
            </button>
            {calcStatus === 'success' && <Check size={14} className="calc-success" />}
            {calcStatus === 'error' && <AlertCircle size={14} className="calc-error" />}
          </div>
        )}

        {calcMessage && (
          <p className={`calc-message ${calcStatus}`}>{calcMessage}</p>
        )}
      </div>

      <TravelBreakdownGroups
        travel={localData}
        rates={rates}
        installDays={installDays}
        laborLines={laborLines}
        formatCurrency={formatCurrency}
        onTravelChange={handlePatch}
      />
    </div>
  );
};

export default TravelInputs;
